import Alert from "@pathwright/ui/src/components/alert/Alert"
import find from "lodash/find"
import uniq from "lodash/uniq"
import pluralize from "pluralize"
import {
  FeatureLockSchoolBillingPlanFragment,
  SchoolBillingPlanFeature,
  useFeatureLockQuery
} from "@pathwright/graphql-client/src/generated"

// FeatureLock determines if the school's subscription includes the appropriate features.
// If no `has` props is provided, FeatureLock simply checks that the account is activated.

// <FeatureLock has={["single-sign-on-sso"]}>
//   {({
//     hasFeatures,
//     activationLocked,
//     lockedFeatures,
//     getPlansNameList
//   }) =>
//     hasFeatures ? (
//       <ManageCommerce />
//     ) : (
//       <Text.Body>
//         Included in the{" "}
//         {getPlansNameList(
//           lockedFeatures["single-sign-on-sso"]
//         )}.
//       </Text.Body>
//     )
//   }
// </FeatureLock>

function getFeatureKeys(
  plans:
    | (FeatureLockSchoolBillingPlanFragment | null | undefined)[]
    | null
    | undefined
) {
  const features = plans
    ?.map((p) => p?.features)
    .reduce((m, n) => m?.concat(n || []), [] as SchoolBillingPlanFeature[])
  const featureKeys = features?.map((f) => f?.key) || []
  return featureKeys
}

// TODO: type the feature keys. These keys are auto-slugified from the feature name
// which means checking keys is brittle!
type FeatureKey = string

type LockedFeatures = Record<FeatureKey, FeatureLockSchoolBillingPlanFragment[]>

type FeatureLockChildren = (args: {
  hasFeatures: boolean
  activationLocked: boolean
  lockedFeatures: LockedFeatures
  getPlansNameList: (plans: FeatureLockSchoolBillingPlanFragment[]) => string
}) => React.ReactNode

function FeatureLock({
  has,
  children,
  loadingElement = null
}: {
  has: FeatureKey | FeatureKey[] | undefined
  children: FeatureLockChildren
  loadingElement?: React.ReactNode
}) {
  const { data, error, loading } = useFeatureLockQuery({
    variables: { default_billing_plan_set: true }
  })
  const account = data?.account
  const plans = account?.billing_plan_set?.plans || []
  const subscription = account?.billing_subscription
  const plan = subscription ? subscription.plan : null

  // Map the plan's order to the order of the plan from which this plan
  // was derived, if possible. It's possible for the plan's order to be inaccurate.
  const planOrder =
    plans.find((p) => p?.id === plan?.plan_derived_from?.id)?.order ||
    plan?.order

  const featureKeys = getFeatureKeys(plans)

  has = ([] as FeatureKey[]).concat(has || featureKeys)

  const invalidFeatureKey = has.find(
    (feature) => featureKeys.indexOf(feature) === -1
  )

  const plansWithFeatures =
    featureKeys && !invalidFeatureKey
      ? has.map((featureKey) => {
          const planWithFeature = plans.find((plan) =>
            plan?.features?.find((f) => f.key == featureKey)
          )
          return {
            feature: featureKey,
            plans: plans.filter(
              (plan) => (plan?.order || 0) >= (planWithFeature?.order || 0)
            )
          }
        })
      : []

  const unlockedFeatures = (() => {
    if (account?.is_activated === false) return null
    if (!plans.length) return null
    const {
      is_on_legacy_plan,
      is_activated_via_override
      // TODO: need is_curriculum_subscriber
      // is_curriculum_subscriber,
    } = account || {}
    const is_curriculum_subscriber =
      account && !account.is_activated && !subscription
    let unlockedPlans = plans.filter(
      (plan) =>
        is_on_legacy_plan ||
        is_activated_via_override ||
        is_curriculum_subscriber ||
        // Assumes two things:
        // 1. this.plan is included in a billing plan set (that orders plans)
        // 2. this.plan.order corresponds to the order of the plans in the billing plan set (whether this.plan exists in the billing plan set or not)
        (plan &&
          plans.find((p) => p?.order == planOrder) &&
          (planOrder || 0) >= plan.order)
    )
    // Also include school's current plan.
    if (plan) {
      unlockedPlans.push(plan)
    }
    const unlocked = uniq(getFeatureKeys(unlockedPlans))
    if (plan && !unlocked.length) {
      console.warn("No features found for this school's plan!")
    }
    return unlocked
  })()

  const lockedFeatures = plansWithFeatures
    .filter(({ feature, plans }) => {
      return unlockedFeatures
        ? !find(unlockedFeatures, (f) => f == feature)
        : true
    })
    .reduce((m, { feature, plans }) => {
      ;(m as any)[feature] = plans
      return m
    }, {} as LockedFeatures)

  const hasFeatures = (() => {
    if (!account) return false
    if (!account.is_activated) return false
    if (!has) return true
    if (account.is_on_legacy_plan) return true
    return !Object.keys(lockedFeatures).length
  })()

  const activationLocked = !account?.is_activated

  function getPlansNameList(plans: FeatureLockSchoolBillingPlanFragment[]) {
    return `${plans
      .map((p, i) => {
        return `${p.name}${
          {
            [plans.length - 1]: " ",
            [plans.length - 2]: " & "
          }[i] || ", "
        }`
      })
      .join("")}${pluralize("Plan", plans.length)}`
  }

  if (loading) return loadingElement
  if (error) return <Alert error={error.message} />

  return children({
    hasFeatures,
    activationLocked,
    lockedFeatures,
    getPlansNameList
  })
}

FeatureLock.displayName = "FeatureLock"

export default FeatureLock
